import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Home as HomeIcon
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { format } from 'date-fns';
import { ru } from 'date-fns/locale';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { clientsAPI, projectsAPI, statusesAPI, tasksAPI, usersAPI } from '../services/api';

export default function Tasks() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const { user } = useAuth();

  const safeText = (value, fallback = '-') => {
    if (value === null || value === undefined) return fallback;
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
      return String(value);
    }
    return fallback;
  };

  const getApiErrorMessage = (err) => {
    const data = err?.response?.data;
    if (!data) return err?.message || '';
    if (typeof data === 'string') return data;
    if (data.error) return data.error;
    if (Array.isArray(data.errors) && data.errors.length > 0) {
      return data.errors.map(e => e?.msg).filter(Boolean).join(', ');
    }
    return err?.message || '';
  };

  const normalizeTaskPayload = (raw) => {
    const payload = { ...raw };

    if (payload.description !== undefined && payload.description !== null) {
      payload.description = String(payload.description);
    }

    if (payload.clientId === '') payload.clientId = null;
    if (payload.deadline === '') payload.deadline = null;

    return payload;
  };
  const [tasks, setTasks] = useState([]);
  const [projects, setProjects] = useState([]);
  const [clients, setClients] = useState([]);
  const [users, setUsers] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [assigneeFilter, setAssigneeFilter] = useState(user?.id || 'all');
  const [deadlineFilter, setDeadlineFilter] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [formData, setFormData] = useState({
    description: '',
    clientId: '',
    projectId: '',
    assigneeId: '',
    statusId: '',
    deadline: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [tasksRes, projectsRes, clientsRes, usersRes, statusesRes] = await Promise.all([
        tasksAPI.getAll(),
        projectsAPI.getAll(),
        clientsAPI.getAll(),
        usersAPI.getAll(),
        statusesAPI.getAll()
      ]);
      setTasks(tasksRes.data);
      setProjects(projectsRes.data);
      setClients(clientsRes.data);
      setUsers(usersRes.data);
      setStatuses(statusesRes.data);
    } catch (err) {
      setError('Ошибка загрузки данных');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (task = null) => {
    if (task) {
      setEditingTask(task);
      setFormData({
        description: task.description,
        clientId: task.clientId || '',
        projectId: task.projectId,
        assigneeId: task.assigneeId,
        statusId: task.statusId,
        deadline: task.deadline ? task.deadline.split('T')[0] : ''
      });
    } else {
      setEditingTask(null);
      setFormData({
        description: '',
        clientId: '',
        projectId: '',
        assigneeId: user?.id || '',
        statusId: statuses[0]?.id || '',
        deadline: ''
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingTask(null);
  };

  const handleSave = async () => {
    try {
      setError('');
      const payload = normalizeTaskPayload(formData);
      if (editingTask) {
        await tasksAPI.update(editingTask.id, payload);
      } else {
        await tasksAPI.create(payload);
      }
      await loadData();
      handleCloseDialog();
    } catch (err) {
      const msg = getApiErrorMessage(err);
      setError(msg ? `Ошибка сохранения: ${msg}` : 'Ошибка сохранения');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Удалить задачу?')) {
      try {
        setError('');
        await tasksAPI.delete(id);
        await loadData();
      } catch (err) {
        const msg = getApiErrorMessage(err);
        setError(msg ? `Ошибка удаления: ${msg}` : 'Ошибка удаления');
      }
    }
  };

  const isOverdue = (deadline) => {
    if (!deadline) return false;
    return new Date(deadline) < new Date();
  };

  const getClientName = (task) => {
    if (!task.clientId) return '-';
    const client = clients.find(c => c.id === task.clientId);
    return client ? safeText(client.companyName, '-') : '-';
  };

  const filteredTasks = tasks
    .filter(t => statusFilter === 'all' || t.statusId === statusFilter)
    .filter(t => assigneeFilter === 'all' || t.assigneeId === assigneeFilter)
    .filter(t => {
      if (deadlineFilter === 'all') return true;
      if (deadlineFilter === 'overdue') return isOverdue(t.deadline);
      return true;
    })
    .sort((a, b) => {
      if (!a.deadline) return 1;
      if (!b.deadline) return -1;
      return new Date(a.deadline) - new Date(b.deadline);
    });

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', maxWidth: '100%', overflowX: 'hidden' }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} gap={1}>
        <Typography variant={isMobile ? "h6" : "h4"} fontWeight="bold">
          Задачи
        </Typography>
        <Box display="flex" gap={1}>
          <Button
            startIcon={<HomeIcon />}
            onClick={() => navigate('/')}
            size="small"
            variant="outlined"
          >
            {!isMobile && 'Главная'}
          </Button>
          <Button
            variant="contained"
            onClick={() => handleOpenDialog()}
            size="small"
          >
            {isMobile ? <AddIcon /> : <>
              <AddIcon sx={{ mr: 1 }} />
              Добавить
            </>}
          </Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Фильтры */}
      <Card sx={{ mb: 2 }}>
        <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
          <Box display="flex" flexDirection="column" gap={1.5}>
            <TextField
              select
              label="Статус"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              fullWidth
              size="small"
            >
              <MenuItem value="all">Все статусы</MenuItem>
              {statuses.map(s => (
                <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Исполнитель"
              value={assigneeFilter}
              onChange={(e) => setAssigneeFilter(e.target.value)}
              fullWidth
              size="small"
            >
              <MenuItem value="all">Все исполнители</MenuItem>
              {users.map(u => (
                <MenuItem key={u.id} value={u.id}>{u.fullName}</MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Дедлайн"
              value={deadlineFilter}
              onChange={(e) => setDeadlineFilter(e.target.value)}
              fullWidth
              size="small"
            >
              <MenuItem value="all">Все</MenuItem>
              <MenuItem value="overdue">Только просроченные</MenuItem>
            </TextField>
          </Box>
        </CardContent>
      </Card>

      {/* Mobile: Cards */}
      {isMobile ? (
        <Box display="flex" flexDirection="column" gap={1.5}>
          {filteredTasks.map((task) => (
            <Card
              key={task.id}
              sx={{
                bgcolor: isOverdue(task.deadline) ? '#ffebee' : 'white',
                border: isOverdue(task.deadline) ? '1px solid #ef5350' : 'none'
              }}
            >
              <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Box display="flex" justifyContent="space-between" alignItems="start" mb={1}>
                  <Typography variant="subtitle2" fontWeight="bold" sx={{ flex: 1, pr: 1, wordBreak: 'break-word' }}>
                    {safeText(task.description)}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    <IconButton size="small" onClick={() => handleOpenDialog(task)} sx={{ p: 0.5 }}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={() => handleDelete(task.id)} sx={{ p: 0.5 }}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>
                <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                  Клиент: {getClientName(task)}
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                  Проект: {safeText(task.project?.name || projects.find(p => p.id === task.projectId)?.name || '-')}
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                  Исполнитель: {safeText(task.assignee?.fullName || users.find(u => u.id === task.assigneeId)?.fullName || '-')}
                </Typography>
                <Box display="flex" gap={1} flexWrap="wrap">
                  <Chip
                    label={safeText(task.status?.name || statuses.find(s => s.id === task.statusId)?.name || 'Статус', 'Статус')}
                    size="small"
                    sx={{
                      bgcolor: task.status?.color || statuses.find(s => s.id === task.statusId)?.color || '#gray',
                      color: 'white',
                      height: 20,
                      fontSize: '0.7rem'
                    }}
                  />
                  {task.deadline && (
                    <Chip
                      label={format(new Date(task.deadline), 'dd.MM.yyyy', { locale: ru })}
                      size="small"
                      color={isOverdue(task.deadline) ? 'error' : 'default'}
                      variant="outlined"
                      sx={{ height: 20, fontSize: '0.7rem' }}
                    />
                  )}
                </Box>
              </CardContent>
            </Card>
          ))}
          {filteredTasks.length === 0 && (
            <Card>
              <CardContent>
                <Typography color="text.secondary" align="center">
                  Нет задач
                </Typography>
              </CardContent>
            </Card>
          )}
        </Box>
      ) : (
        /* Desktop: Table */
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Описание</TableCell>
                <TableCell>Клиент</TableCell>
                <TableCell>Проект</TableCell>
                <TableCell>Исполнитель</TableCell>
                <TableCell>Статус</TableCell>
                <TableCell>Дедлайн</TableCell>
                <TableCell align="right">Действия</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredTasks.map((task) => (
                <TableRow
                  key={task.id}
                  hover
                  sx={{
                    bgcolor: isOverdue(task.deadline) ? '#ffebee' : 'inherit',
                    '&:hover': {
                      bgcolor: isOverdue(task.deadline) ? '#ffcdd2 !important' : 'action.hover'
                    }
                  }}
                >
                  <TableCell sx={{ wordBreak: 'break-word', maxWidth: '300px' }}>{safeText(task.description)}</TableCell>
                  <TableCell>{getClientName(task)}</TableCell>
                  <TableCell>{safeText(task.project?.name || projects.find(p => p.id === task.projectId)?.name || '-')}</TableCell>
                  <TableCell>{safeText(task.assignee?.fullName || users.find(u => u.id === task.assigneeId)?.fullName || '-')}</TableCell>
                  <TableCell>
                    <Chip
                      label={safeText(task.status?.name || statuses.find(s => s.id === task.statusId)?.name || 'Статус', 'Статус')}
                      size="small"
                      sx={{
                        bgcolor: task.status?.color || statuses.find(s => s.id === task.statusId)?.color || '#gray',
                        color: 'white'
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    {task.deadline ? (
                      <Chip
                        label={format(new Date(task.deadline), 'dd.MM.yyyy', { locale: ru })}
                        size="small"
                        color={isOverdue(task.deadline) ? 'error' : 'default'}
                        variant="outlined"
                      />
                    ) : '-'}
                  </TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={() => handleOpenDialog(task)}>
                      <EditIcon />
                    </IconButton>
                    <IconButton size="small" onClick={() => handleDelete(task.id)}>
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {filteredTasks.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Typography color="text.secondary">Нет задач</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>
          {editingTask ? 'Редактировать задачу' : 'Добавить задачу'}
        </DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <TextField
              label="Описание"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              required
              fullWidth
              size="small"
              multiline
              rows={8}
            />
            <TextField
              select
              label="Клиент"
              value={formData.clientId}
              onChange={(e) => setFormData({ ...formData, clientId: e.target.value })}
              fullWidth
              size="small"
            >
              <MenuItem value="">Не выбран</MenuItem>
              {clients.map(c => (
                <MenuItem key={c.id} value={c.id}>{c.companyName}</MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Проект"
              value={formData.projectId}
              onChange={(e) => setFormData({ ...formData, projectId: e.target.value })}
              required
              fullWidth
              size="small"
            >
              {projects.map(p => (
                <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Исполнитель"
              value={formData.assigneeId}
              onChange={(e) => setFormData({ ...formData, assigneeId: e.target.value })}
              required
              fullWidth
              size="small"
            >
              {users.map(u => (
                <MenuItem key={u.id} value={u.id}>{u.fullName}</MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Статус"
              value={formData.statusId}
              onChange={(e) => setFormData({ ...formData, statusId: e.target.value })}
              required
              fullWidth
              size="small"
            >
              {statuses.map(s => (
                <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
              ))}
            </TextField>
            <TextField
              label="Дедлайн"
              type="date"
              value={formData.deadline}
              onChange={(e) => setFormData({ ...formData, deadline: e.target.value })}
              InputLabelProps={{ shrink: true }}
              fullWidth
              size="small"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Отмена</Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={!formData.description || !formData.projectId}
          >
            Сохранить
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
